<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import type { PropType } from 'vue';
import type { Api } from '@/modules/borrower/types/borrower-loans';
import ItemsBatchConfirmationDialog from '@/modules/common/components/ItemsBatchConfirmationDialog.vue';
import type { NewQuantities } from '@/modules/open-loans/helpers/multipleUpdates';
import type { OpenLoanItem } from '@/modules/common/models';
import { isAfterCutOffTime as isAfterCutOff, useCurrentTime } from '@/composables/current-time';
import { store as vuex } from '@/store/store';

type LoanReturnRequestItem = Api.LoanReturnRequestItem;

const props = defineProps({
  items: { type: Array as PropType<OpenLoanItem[]>, required: true },
  newQuantities: {
    type: Object as PropType<NewQuantities>,
    required: true,
    default: () => ({}),
  },
});

const { currentTimeUTC } = useCurrentTime();
const timezoneOffset = computed(() => vuex.state.clientConfig?.timezoneOffset);

const instance = getCurrentInstance()!;

const tableHeaders = [
  { text: 'Counterparty' },
  { text: 'Ticker' },
  { text: 'CUSIP' },
  { text: 'Rebate Rate', align: 'end' },
  { text: `Return Quantity`, value: 'toUpdate', align: 'end' },
  { text: 'Available To Return', value: 'openQuantityToReturn', align: 'end' },
];

function isAfterCutOffTime(loan: OpenLoanItem): boolean {
  return isAfterCutOff(loan, currentTimeUTC.value, timezoneOffset.value);
}

const totalQuantity = computed(() =>
  Object.values(props.newQuantities).reduce((acc, val) => acc + val, 0)
);

const atLeastOneAfterCutOffTime = computed(() => props.items.some(isAfterCutOffTime));

async function execute(): Promise<{ failedIds?: string[] }> {
  const returns = props.items.map(
    (loan) =>
      ({
        displayId: loan.displayId,
        quantity: props.newQuantities[loan.id],
      }) as LoanReturnRequestItem
  );
  const result = await instance.proxy!.$api.borrowerLoans.batchReturnBorrowerLoan(returns);
  const failedIds = result.failedItems.map((i) => i.requestId);

  // failing ids only, we are losing the error messages here
  return { failedIds };
}
</script>

<template>
  <!-- template must have 1 direct child, we wrap the contents in a <div>
       with "display: contents", making sure layout rendering is not affected -->
  <div style="display: contents">
    <ItemsBatchConfirmationDialog
      :execute="execute"
      item-id-key="displayId"
      :items="items"
      success-message="Returns successfully initiated."
      :table-headers="tableHeaders"
      title="Return Loans"
      v-on="$listeners"
    >
      <template #description>
        You are about to return <strong><PrettyNumber :value="totalQuantity" /></strong> shares
        across <strong><PrettyNumber :value="items.length" /></strong> loans:
      </template>
      <template #row="{ item }">
        <td>
          <span :class="{ 'orange--text': isAfterCutOffTime(item) }">{{
            item.counterpartyDisplay
          }}</span>
        </td>
        <td>
          {{ item.security.ticker }}
        </td>
        <td>
          {{ item.security.cusip }}
        </td>
        <td class="text-end">
          <RateOutput :rate="item.rate" :rate-modifier="item.rateModifier" />
        </td>
        <td class="text-end">
          <PrettyNumber :value="newQuantities[item.id] || 0" />
        </td>
        <td class="text-end">
          <PrettyNumber :value="item.openQuantityToReturn" />
        </td>
      </template>
      <template #disclaimer>
        <VAlert
          v-if="atLeastOneAfterCutOffTime"
          class="mt-8 ml-8 mr-8 mb-4"
          outlined
          type="warning"
        >
          Some returns initiated will require the lender to approve the return before they get
          officially recorded to the trade and/or settlement instructions sent.
        </VAlert>
      </template>
    </ItemsBatchConfirmationDialog>
  </div>
</template>

<style lang="scss" scoped></style>
